import { useState, useEffect } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Shield, Phone, MapPin, Users, AlertTriangle, Check, Edit } from 'lucide-react';
import { toast } from 'sonner';

const SafetyFeatures = ({ currentCheckin, friends }) => {
  const [emergencyContact, setEmergencyContact] = useState({ name: '', phone: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    fetchEmergencyContact();
  }, []);

  const fetchEmergencyContact = async () => {
    try {
      const response = await axios.get(`${API}/safety/emergency-contact`);
      if (response.data.emergency_contact_name) {
        setEmergencyContact({
          name: response.data.emergency_contact_name,
          phone: response.data.emergency_contact_phone
        });
      }
    } catch (e) {
      console.error('Failed to fetch emergency contact:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveContact = async (e) => {
    e.preventDefault();
    if (!emergencyContact.name || !emergencyContact.phone) {
      toast.error('Please fill in both name and phone number');
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/safety/emergency-contact`, null, {
        params: {
          name: emergencyContact.name,
          phone: emergencyContact.phone
        }
      });
      toast.success('Emergency contact saved!');
      setIsEditDialogOpen(false);
    } catch (e) {
      console.error('Failed to save emergency contact:', e);
      toast.error('Failed to save emergency contact');
    } finally {
      setSaving(false);
    }
  };

  const handleShareLocation = async () => {
    if (selectedFriends.length === 0) {
      toast.error('Please select at least one friend');
      return;
    }

    if (!currentCheckin) {
      toast.error('You need to be checked in to share your location');
      return;
    }

    setSharing(true);
    try {
      await axios.post(`${API}/safety/share-location`, {
        checkin_id: currentCheckin.id,
        share_with: selectedFriends
      });
      toast.success('Location shared with your friends!');
      setIsShareDialogOpen(false);
      setSelectedFriends([]);
    } catch (e) {
      console.error('Failed to share location:', e);
      toast.error('Failed to share location');
    } finally {
      setSharing(false);
    }
  };

  if (loading) {
    return null;
  }

  return (
    <Card className="glass p-6 border border-orange-500/30 fade-in">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <Shield className="w-5 h-5 text-orange-400" />
        Safety Features
      </h3>

      <div className="space-y-4">
        {/* Emergency Contact */}
        <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-red-400" />
              <span className="text-sm font-medium text-white">Emergency Contact</span>
            </div>
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-purple-400 hover:text-purple-300 p-1 h-auto"
                >
                  <Edit className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent className="glass border border-slate-700">
                <DialogHeader>
                  <DialogTitle className="text-white">Set Emergency Contact</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveContact} className="space-y-4">
                  <div>
                    <label className="text-sm text-slate-300 mb-2 block">Contact Name</label>
                    <Input
                      value={emergencyContact.name}
                      onChange={(e) => setEmergencyContact({ ...emergencyContact, name: e.target.value })}
                      placeholder="e.g., Mom, Best Friend"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300 mb-2 block">Phone Number</label>
                    <Input
                      value={emergencyContact.phone}
                      onChange={(e) => setEmergencyContact({ ...emergencyContact, phone: e.target.value })}
                      placeholder="+1 (555) 123-4567"
                      className="bg-slate-800 border-slate-700 text-white"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-red-600 hover:bg-red-700"
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Contact'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {emergencyContact.name ? (
            <div className="text-sm text-slate-300">
              <p className="font-medium">{emergencyContact.name}</p>
              <p className="text-slate-400">{emergencyContact.phone}</p>
            </div>
          ) : (
            <p className="text-sm text-slate-400">No emergency contact set</p>
          )}
        </div>

        {/* Share Location */}
        <Dialog open={isShareDialogOpen} onOpenChange={setIsShareDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600"
              data-testid="share-location-btn"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Share My Location
            </Button>
          </DialogTrigger>
          <DialogContent className="glass border border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <MapPin className="w-5 h-5 text-orange-400" />
                Share Location with Friends
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {!currentCheckin ? (
                <div className="text-center py-4">
                  <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-2" />
                  <p className="text-slate-300">You need to be checked in to a bar to share your location.</p>
                </div>
              ) : friends && friends.length > 0 ? (
                <>
                  <p className="text-sm text-slate-300">
                    Select friends to notify about your location:
                  </p>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {friends.map((friend) => (
                      <label
                        key={friend.id}
                        className="flex items-center gap-3 p-3 bg-slate-800/50 rounded-lg cursor-pointer hover:bg-slate-800 transition-colors"
                      >
                        <Checkbox
                          checked={selectedFriends.includes(friend.id)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedFriends([...selectedFriends, friend.id]);
                            } else {
                              setSelectedFriends(selectedFriends.filter(id => id !== friend.id));
                            }
                          }}
                        />
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-semibold">
                              {friend.username?.[0]?.toUpperCase() || 'U'}
                            </span>
                          </div>
                          <span className="text-white">{friend.username}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                  <Button
                    onClick={handleShareLocation}
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    disabled={sharing || selectedFriends.length === 0}
                  >
                    {sharing ? (
                      'Sharing...'
                    ) : (
                      <>
                        <Users className="w-4 h-4 mr-2" />
                        Share with {selectedFriends.length} Friend(s)
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <div className="text-center py-4">
                  <Users className="w-12 h-12 text-slate-500 mx-auto mb-2" />
                  <p className="text-slate-300">No friends to share with.</p>
                  <p className="text-sm text-slate-400">Add some friends first!</p>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Safety Tips */}
        <div className="text-xs text-slate-400 space-y-1">
          <p className="flex items-center gap-1">
            <Check className="w-3 h-3 text-green-400" />
            Always let someone know where you are
          </p>
          <p className="flex items-center gap-1">
            <Check className="w-3 h-3 text-green-400" />
            Use the buddy system when going out
          </p>
          <p className="flex items-center gap-1">
            <Check className="w-3 h-3 text-green-400" />
            Trust your instincts if something feels off
          </p>
        </div>
      </div>
    </Card>
  );
};

export default SafetyFeatures;
